import { ActionStatus } from './action-status.enum';
import { IssueStatusEnum } from './issue-status.enum';
import { ROLE, Role } from './role';

export const BASIC_ACTION_FIELDS = {
  ID: 'id',
  TITLE: 'title',
  DESCRIPTION: 'description',
  STATUS: 'status',
  DUE_DATE: 'dueDate',
  CREATED_AT: 'createdAt',
  UPDATED_AT: 'updatedAt',
  SEQUENCE: 'sequence',
  OWNER: 'owner',
  APPROVER: 'approver',
  ATTACHMENTS: 'attachments',
  GOVMAP_GROUPS: 'govmapGroups',
} as const;

export const ACTION_APPROVAL_FIELDS = {
  APPROVAL_STATUS: 'approvalStatus',
  DECLINE_NOTE: 'declineNote',
} as const;

export type BasicActionField =
  (typeof BASIC_ACTION_FIELDS)[keyof typeof BASIC_ACTION_FIELDS];
export type ActionApprovalField =
  (typeof ACTION_APPROVAL_FIELDS)[keyof typeof ACTION_APPROVAL_FIELDS];

export type ActionFieldValue = BasicActionField | ActionApprovalField;

export const basicActionFields: BasicActionField[] =
  Object.values(BASIC_ACTION_FIELDS);
export const actionApprovalFields: ActionApprovalField[] = Object.values(
  ACTION_APPROVAL_FIELDS,
);

export const actionFields: readonly ActionFieldValue[] = [
  ...basicActionFields,
  ...actionApprovalFields,
] as const;

const earlyActionRoles: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.OWNER,
  ROLE.APPROVER,
  ROLE.WATCHER,
] as const;

const allActionRoles: readonly Role[] = [
  ROLE.ISSUE_TYPE_MANAGER,
  ROLE.CREATOR,
  ROLE.OWNER,
  ROLE.APPROVER,
  ROLE.WATCHER,
  ROLE.ACTION_OWNER,
  ROLE.ACTION_APPROVER,
] as const;

export const ACTION_READABLE_FIELDS: Record<
  ActionStatus,
  {
    roles: readonly Role[];
    fields: readonly ActionFieldValue[];
    issueStatuses: readonly IssueStatusEnum[];
  }
> = {
  [ActionStatus.DRAFT]: {
    roles: earlyActionRoles,
    fields: actionFields,
    issueStatuses: [
      // In Draft from template
      IssueStatusEnum.DRAFT,
      IssueStatusEnum.IN_REVIEW,
      IssueStatusEnum.TO_BE_PUBLISHED,
      IssueStatusEnum.PENDING_ACCEPTANCE,
      // in progress here is remediation planning phase
      IssueStatusEnum.IN_PROGRESS,
    ],
  },
  [ActionStatus.PENDING]: {
    roles: earlyActionRoles,
    fields: actionFields,
    // in progress here is in remediation phase
    issueStatuses: [IssueStatusEnum.IN_PROGRESS],
  },
  [ActionStatus.OPEN]: {
    roles: allActionRoles,
    fields: actionFields,
    // in progress here is in remediation phase
    issueStatuses: [IssueStatusEnum.IN_PROGRESS],
  },
  [ActionStatus.IN_PROGRESS]: {
    roles: allActionRoles,
    fields: actionFields,
    // in progress here is in remediation phase
    issueStatuses: [IssueStatusEnum.IN_PROGRESS],
  },
  [ActionStatus.TO_BE_APPROVED]: {
    roles: allActionRoles,
    fields: actionFields,
    // in progress here is in remediation phase
    issueStatuses: [IssueStatusEnum.IN_PROGRESS],
  },
  [ActionStatus.COMPLETED]: {
    roles: allActionRoles,
    fields: actionFields,
    // in progress here is in remediation phase
    issueStatuses: [IssueStatusEnum.IN_PROGRESS, IssueStatusEnum.CLOSED],
  },
  [ActionStatus.DISCARDED]: {
    roles: earlyActionRoles,
    fields: actionFields,
    issueStatuses: [IssueStatusEnum.DISCARDED],
  },
} as const;
